import { type ReactNode, useState } from 'react';

import { Avatar } from '@playbooks/molecules/avatars';
import { Skeleton } from '@playbooks/molecules/skeletons';
import { Badge } from '@playbooks/ui/components/badge';
import { Button } from '@playbooks/ui/components/button';
import { FadIcon, Icon } from '@playbooks/ui/components/icon';
import { Link } from '@playbooks/ui/components/link';
import { cn } from '@playbooks/ui/lib/utils';
import { computeBytes, formatDate, timeElapsed, toCurrency, toShortNumber } from '@playbooks/utils';

type ItemValueFormat =
	| 'avatar'
	| 'badge'
	| 'badges'
	| 'boolean'
	| 'bytes'
	| 'currency'
	| 'date'
	| 'date-range'
	| 'duration'
	| 'html'
	| 'icon'
	| 'json'
	| 'link'
	| 'number'
	| 'password'
	| 'photo'
	| 'stat'
	| 'text';

type ItemValueBaseProps = {
	layout?: 'default' | 'split';
	loading?: boolean;
	emptyValue?: ReactNode;
	className?: string;
	children?: ReactNode;
};

type ItemValueProps = ItemValueBaseProps &
	(
		| { format?: 'text'; value?: ReactNode }
		| { format: 'avatar'; value?: { thumbnail?: string; name?: string; title?: string; webUrl?: string } | null }
		| { format: 'badge'; value?: string | number | boolean | null }
		| { format: 'badges'; value?: Array<string | number> | null }
		| { format: 'boolean'; value?: boolean | null }
		| { format: 'bytes'; value?: number | null; bytesUnit?: string }
		| { format: 'currency' | 'number'; value?: number | null }
		| { format: 'date'; value?: string | number | Date | null }
		| { format: 'date-range' | 'duration'; values?: Array<string | number | Date | null> }
		| { format: 'html'; value?: string | null }
		| { format: 'icon' | 'photo'; value?: string | null }
		| { format: 'json'; value?: unknown }
		| { format: 'link'; value?: ReactNode; href?: string }
		| { format: 'password'; value?: string | null }
		| { format: 'stat'; value?: ReactNode }
	);

type ItemValueInternalProps = ItemValueBaseProps & {
	format?: ItemValueFormat;
	layout?: 'default' | 'split';
	value?: any;
	values?: any[];
	href?: string;
	bytesUnit?: string;
};

function ItemValue(props: ItemValueProps) {
	const {
		format = 'text',
		layout = 'default',
		value,
		values = [],
		href,
		loading = false,
		emptyValue = '—',
		bytesUnit = 'kb',
		className = '',
		children,
	} = props as ItemValueInternalProps;
	const [revealed, setRevealed] = useState(false);

	// Computed
	const isEmpty = value === null || value === undefined || value === '';

	// Theme
	const rootClassName = cn(
		'flex min-w-0 max-w-2/3 shrink-0 items-center justify-end gap-2 text-right text-sm text-foreground',
		layout === 'split' && 'w-2/3',
		format === 'html' || format === 'json' ? 'max-w-full overflow-x-auto' : 'truncate',
		className,
	);

	// Render
	if (loading) return <Skeleton className='h-4 w-20 shrink-0' />;
	if (children) return <div className={rootClassName}>{children}</div>;

	let content: ReactNode = isEmpty ? emptyValue : value;
	if (!isEmpty) {
		switch (format) {
			case 'avatar':
				content = (
					<Avatar
						layout='meta'
						icon=''
						thumbnail={value?.thumbnail}
						title={value?.name || value?.title}
						href={value?.webUrl || ''}
					/>
				);
				break;
			case 'badge':
				content = <Badge variant={value}>{String(value)}</Badge>;
				break;
			case 'badges':
				content =
					Array.isArray(value) && value.length ? (
						<div className='flex flex-wrap justify-end gap-1'>
							{value.map((badge, index) => (
								<Badge key={`${badge}-${index}`} variant={badge}>
									{String(badge)}
								</Badge>
							))}
						</div>
					) : (
						emptyValue
					);
				break;
			case 'boolean':
				content = (
					<FadIcon icon={value ? 'circle-check' : 'circle-xmark'} className={value ? 'text-success' : 'text-danger'} />
				);
				break;
			case 'bytes':
				content = computeBytes(value, bytesUnit);
				break;
			case 'currency':
				content = toCurrency(value);
				break;
			case 'date':
				content = formatDate(value);
				break;
			case 'date-range':
				content =
					values.length >= 2
						? `${formatDate(values[0], 'MMMM Do, YYYY')} – ${formatDate(values[1], 'MMMM Do, YYYY')}`
						: emptyValue;
				break;
			case 'duration':
				content = values.length >= 2 ? timeElapsed(values[0], values[1]) : emptyValue;
				break;
			case 'html':
				content = <div dangerouslySetInnerHTML={{ __html: String(value) }} className='prose-editorial text-left' />;
				break;
			case 'icon':
				content = <FadIcon icon={value} />;
				break;
			case 'json':
				content = <code className='whitespace-pre-wrap break-all text-left'>{JSON.stringify(value)}</code>;
				break;
			case 'link':
				content = href ? (
					<Link variant='link' href={href} className='min-w-0 truncate text-action hover:underline'>
						{String(value || 'View')}
						<Icon data-icon='inline-end' type='far' icon='arrow-up-right' aria-hidden />
					</Link>
				) : (
					emptyValue
				);
				break;
			case 'number':
				content = toShortNumber(value);
				break;
			case 'password':
				content = (
					<>
						<span>{revealed ? String(value) : '••••••••'}</span>
						<Button
							variant='secondary'
							size='icon-xs'
							aria-label={revealed ? 'Hide value' : 'Reveal value'}
							onClick={() => setRevealed(current => !current)}>
							<Icon type='far' icon={revealed ? 'eye-slash' : 'eye'} aria-hidden />
						</Button>
					</>
				);
				break;
			case 'photo':
				content = <img className='size-6 rounded-control object-cover' src={value} alt='' />;
				break;
			case 'stat':
				content = <strong className='type-section-title'>{String(value)}</strong>;
				break;
		}
	}

	return <div className={rootClassName}>{content}</div>;
}

export { ItemValue };
export type { ItemValueFormat, ItemValueProps };
